// src/components/Analytics.tsx - FIXED: Handle missing endpoint gracefully
import React, { useState, useEffect, useCallback } from 'react';
import './Analytics.css';
import { 
  BarChart3, 
  Clock, 
  AlertTriangle, 
  Activity, 
  Calendar,
  ArrowLeft,
  RefreshCw,
  Server,
  Target,
  Zap
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useAppContext } from '../hooks/useAppContext';
import JobsTable from './JobsTable';
import { API_BASE_URL } from '../constants';

interface AnalyticsData {
  api_key: string;
  time_window_hours: number;
  requests: {
    total: number;
    unique_endpoints: number;
    avg_payload_size: number;
  };
  responses: {
    total: number;
    avg_response_time_ms: number;
    error_count: number;
    rate_limit_hits: number;
    error_rate: number;
  };
  top_endpoints: Array<{
    endpoint: string;
    count: number;
  }>;
  status_codes: Array<{
    status_code: number;
    count: number;
  }>;
  rate_limiting: {
    api_key: string;
    current_usage: {
      minute: number;
      hour: number;
      day: number;
    };
    limits: {
      minute: number;
      hour: number;
      day: number;
    };
    remaining: {
      minute: number;
      hour: number;
      day: number;
    };
    reset_times: {
      minute: number;
      hour: number;
      day: number;
    };
  };
}

const Analytics: React.FC = () => {
  const { apiKey } = useAuth();
  const { setView } = useAppContext();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeWindow, setTimeWindow] = useState<number>(24);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [usingMockData, setUsingMockData] = useState(false);

  const fetchAnalytics = useCallback(async (hours: number = timeWindow) => {
    setLoading(true);
    setError(null);
    setUsingMockData(false);
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/analytics/self?time_window_hours=${hours}`,
        {
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 404) {
        // Expected - endpoint not implemented yet
        setUsingMockData(true);
        
        // Generate consistent mock data
        const now = Date.now();
        const mockData: AnalyticsData = {
          api_key: apiKey || 'demo_key',
          time_window_hours: hours,
          requests: {
            total: Math.floor(500 + Math.random() * 500),
            unique_endpoints: 8,
            avg_payload_size: Math.floor(2048 + Math.random() * 3000)
          },
          responses: {
            total: Math.floor(500 + Math.random() * 500),
            avg_response_time_ms: Math.floor(150 + Math.random() * 350),
            error_count: Math.floor(Math.random() * 20),
            rate_limit_hits: Math.floor(Math.random() * 5),
            error_rate: Math.random() * 5
          },
          top_endpoints: [
            { endpoint: '/api/search', count: Math.floor(100 + Math.random() * 100) },
            { endpoint: '/api/search_metadata', count: Math.floor(50 + Math.random() * 100) },
            { endpoint: '/api/jobs', count: Math.floor(25 + Math.random() * 75) }
          ],
          status_codes: [
            { status_code: 200, count: Math.floor(300 + Math.random() * 100) },
            { status_code: 201, count: Math.floor(20 + Math.random() * 30) },
            { status_code: 400, count: Math.floor(5 + Math.random() * 15) }
          ],
          rate_limiting: {
            api_key: apiKey || 'demo_key',
            current_usage: {
              minute: Math.floor(Math.random() * 45),
              hour: Math.floor(Math.random() * 800),
              day: Math.floor(Math.random() * 4500)
            },
            limits: { minute: 60, hour: 1000, day: 5000 },
            remaining: {
              minute: 60 - Math.floor(Math.random() * 45),
              hour: 1000 - Math.floor(Math.random() * 800),
              day: 5000 - Math.floor(Math.random() * 4500)
            },
            reset_times: {
              minute: Math.floor((now + 60000) / 1000),
              hour: Math.floor((now + 3600000) / 1000),
              day: Math.floor((now + 86400000) / 1000)
            }
          }
        };
        
        setAnalyticsData(mockData);
        setLastRefresh(new Date());
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Ensure rate_limiting exists
      if (!data.rate_limiting) {
        const now = Date.now();
        data.rate_limiting = {
          api_key: apiKey,
          current_usage: {
            minute: 0,
            hour: 0,
            day: 0
          },
          limits: { minute: 60, hour: 1000, day: 5000 },
          remaining: {
            minute: 60,
            hour: 1000,
            day: 5000
          },
          reset_times: {
            minute: Math.floor((now + 60000) / 1000),
            hour: Math.floor((now + 3600000) / 1000),
            day: Math.floor((now + 86400000) / 1000)
          }
        };
      }
      
      setAnalyticsData(data);
      setLastRefresh(new Date());
    } catch (err) {
      // Only log non-404 errors
      if (err instanceof Error && !err.message.includes('404')) {
        console.error('Analytics error:', err);
        setError('Unable to load analytics data');
      } else {
        // Silently fall back to mock data for any other errors
        setUsingMockData(true);
        fetchAnalytics(hours); // Recursive call will hit the 404 handler
      }
    } finally {
      setLoading(false);
    }
  }, [apiKey, timeWindow]);

  useEffect(() => {
    if (apiKey) {
      fetchAnalytics();
    }
  }, [timeWindow, apiKey, fetchAnalytics]);

  const handleRefresh = () => {
    fetchAnalytics();
  };

  const handleTimeWindowChange = (hours: number) => {
    setTimeWindow(hours);
  };

  const formatNumber = (num: number | undefined): string => {
    if (num === undefined || num === null || isNaN(num)) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatPercent = (num: number | undefined): string => {
    if (num === undefined || num === null || isNaN(num)) return '0.0%';
    return num.toFixed(1) + '%';
  };

  const getStatusColor = (status: number): string => {
    if (status >= 200 && status < 300) return '#10b981';
    if (status >= 400 && status < 500) return '#f59e0b';
    if (status >= 500) return '#ef4444';
    return '#6b7280';
  };

  const getEndpointDisplayName = (endpoint: string): string => {
    const cleanEndpoint = endpoint.replace(/^\/api\//, '').replace(/\/$/, '');
    if (!cleanEndpoint) return 'Root';
    return cleanEndpoint.split('/').map(part => 
      part.charAt(0).toUpperCase() + part.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <div className="section">
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => setView('main')} className="back-button">
            <ArrowLeft size={20} />
            Back
          </button>
          <h1 className="text-2xl font-bold">Analytics</h1>
        </div>
        <div className="loading-section">
          <RefreshCw size={32} className="loading-spinner" />
          <p>Loading analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="section">
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => setView('main')} className="back-button">
            <ArrowLeft size={20} />
            Back
          </button>
          <h1 className="text-2xl font-bold">Analytics</h1>
        </div>
        <div className="error-container">
          <AlertTriangle size={32} />
          <h3>Error Loading Analytics</h3>
          <p>{error}</p>
          <button onClick={handleRefresh} className="btn-retry">
            <RefreshCw size={16} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <button onClick={() => setView('main')} className="back-button">
          <ArrowLeft size={20} />
          Back
        </button>
        <div className="header-content">
          <h1>API Usage Analytics</h1>
          <p className="header-subtitle">
            Showing data for the last {analyticsData?.time_window_hours || 24} hours
            {usingMockData && <span className="mock-data-indicator"> (Demo Data)</span>}
          </p>
        </div>
        <div className="analytics-controls">
          <div className="time-window-selector">
            <span>Time Window:</span>
            {[1, 6, 24, 168].map((hours) => (
              <button
                key={hours}
                onClick={() => handleTimeWindowChange(hours)}
                className={`btn-filter ${timeWindow === hours ? 'active' : ''}`}
              >
                {hours === 1 ? '1h' : hours === 6 ? '6h' : hours === 24 ? '24h' : '7d'}
              </button>
            ))}
          </div>
          <button onClick={handleRefresh} className="btn-refresh">
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {analyticsData && (
        <div>
          {/* Key Metrics */}
          <div className="grid grid-2" style={{ marginBottom: 32 }}>
            <div className="metric-card primary">
              <div className="metric-icon">
                <Activity size={28} />
              </div>
              <div className="metric-content">
                <h3>Total Requests</h3>
                <p className="metric-value">{formatNumber(analyticsData.requests?.total)}</p>
                <p className="metric-subtitle">
                  {formatNumber(analyticsData.responses?.total)} responses
                </p>
              </div>
            </div>

            <div className="metric-card card">
              <div className="metric-icon">
                <Clock size={28} />
              </div>
              <div className="metric-content">
                <h3>Avg Response Time</h3>
                <p className="metric-value">
                  {Math.round(analyticsData.responses?.avg_response_time_ms || 0)}ms
                </p>
                <p className="metric-subtitle">
                  {formatNumber(analyticsData.requests?.avg_payload_size)} avg bytes
                </p>
              </div>
            </div>

            <div className="metric-card card">
              <div className="metric-icon">
                <AlertTriangle size={28} />
              </div>
              <div className="metric-content">
                <h3>Error Rate</h3>
                <p className="metric-value">{formatPercent(analyticsData.responses?.error_rate)}</p>
                <p className="metric-subtitle">
                  {formatNumber(analyticsData.responses?.error_count)} errors
                </p>
              </div>
            </div>

            <div className="metric-card card">
              <div className="metric-icon">
                <Target size={28} />
              </div>
              <div className="metric-content">
                <h3>Unique Endpoints</h3>
                <p className="metric-value">{formatNumber(analyticsData.requests?.unique_endpoints)}</p>
                <p className="metric-subtitle">
                  {formatNumber(analyticsData.responses?.rate_limit_hits)} rate limits
                </p>
              </div>
            </div>
          </div>

          {/* Rate Limiting */}
          <div className="section rate-limit-section">
            <div className="section-header">
              <h3 className="section-title">
                <Zap size={20} />
                Rate Limiting Status
              </h3>
            </div>
            <div className="rate-limit-cards">
              {['minute', 'hour', 'day'].map((period) => (
                <div key={period} className="rate-limit-card">
                  <h4>Per {period.charAt(0).toUpperCase() + period.slice(1)}</h4>
                  <div className="rate-progress">
                    <div className="rate-numbers">
                      <span>{analyticsData.rate_limiting?.current_usage?.[period as keyof typeof analyticsData.rate_limiting.current_usage] || 0}</span>
                      <span>/</span>
                      <span>{analyticsData.rate_limiting?.limits?.[period as keyof typeof analyticsData.rate_limiting.limits] || 0}</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ 
                          width: `${Math.min(100, ((analyticsData.rate_limiting?.current_usage?.[period as keyof typeof analyticsData.rate_limiting.current_usage] || 0) / (analyticsData.rate_limiting?.limits?.[period as keyof typeof analyticsData.rate_limiting.limits] || 1)) * 100)}%` 
                        }}
                      />
                    </div>
                    <span className="remaining">{analyticsData.rate_limiting?.remaining?.[period as keyof typeof analyticsData.rate_limiting.remaining] || 0} remaining</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-2" style={{ marginBottom: 32 }}>
            <div className="card">
              <div className="card-header">
                <h3 className="section-title">
                  <Server size={20} />
                  Top Endpoints
                </h3>
              </div>
              <div className="card-body">
                <div className="chart-content">
                  {analyticsData.top_endpoints?.length > 0 ? (
                    analyticsData.top_endpoints.slice(0, 8).map((endpoint, index) => {
                      const percentage = ((endpoint.count || 0) / (analyticsData.requests?.total || 1)) * 100;
                      return (
                        <div key={`${endpoint.endpoint}-${index}`} className="chart-item">
                          <div className="chart-item-header">
                            <span className="chart-item-label">
                              {getEndpointDisplayName(endpoint.endpoint)}
                            </span>
                            <span className="chart-item-value">
                              {formatNumber(endpoint.count)} ({formatPercent(percentage)})
                            </span>
                          </div>
                          <div className="chart-bar">
                            <div 
                              className="chart-bar-fill"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="no-data">No endpoint data available</div>
                  )}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="section-title">
                  <BarChart3 size={20} />
                  HTTP Status Codes
                </h3>
              </div>
              <div className="card-body">
                <div className="chart-content">
                  {analyticsData.status_codes?.length > 0 ? (
                    analyticsData.status_codes.map((status, index) => {
                      const percentage = ((status.count || 0) / (analyticsData.responses?.total || 1)) * 100;
                      return (
                        <div key={`${status.status_code}-${index}`} className="chart-item">
                          <div className="chart-item-header">
                            <span 
                              className="chart-item-label status-code"
                              style={{ color: getStatusColor(status.status_code) }}
                            >
                              {status.status_code}
                            </span>
                            <span className="chart-item-value">
                              {formatNumber(status.count)} ({formatPercent(percentage)})
                            </span>
                          </div>
                          <div className="chart-bar">
                            <div 
                              className="chart-bar-fill"
                              style={{ 
                                width: `${percentage}%`,
                                backgroundColor: getStatusColor(status.status_code)
                              }}
                            />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="no-data">No status code data available</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Jobs Table */}
          <div className="section">
            <div className="section-header">
              <h3 className="section-title">
                <Activity size={20} />
                Your Job Activity
              </h3>
              <span className="section-subtitle">All your background jobs ordered by most recent</span>
            </div>
            
            <JobsTable 
              isAdminMode={false}
              showUserColumn={false}
              emptyStateTitle="No Jobs Found"
              emptyStateDescription="Background jobs will appear here once you start running operations"
              maxHeight="500px"
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600 mt-8">
            <Calendar size={16} />
            <span>Last updated: {lastRefresh.toLocaleTimeString()}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;